import type { Persona } from './cast.js';
import type { StyleId } from './style.js';
import { TABLE_BANKS, type TableEvent } from './banks-table.js';

/**
 * Authored commentary banks (docs/proposals/personas-slices-execution.md, P3): every line is
 * written by hand — no LLM rephrasing (P8 stays dropped). Kid tone throughout, tu-form (FR) /
 * tú-form (ES), each persona in its own voice. v1 ships four core events; more events join per
 * game as the screens wire them (P6).
 *
 * `encourage` fires when the CHILD makes a mistake — it comforts, never scolds.
 */
export type CoreEvent = 'game-start' | 'win' | 'lose' | 'encourage';
export type PersonaEvent = CoreEvent | TableEvent;

export type Locale = 'en' | 'fr' | 'es';

/**
 * One authored line, either plain (fits every style) or tagged with the
 * styles it expresses (P5 "talk and moves agree": lines are tagged with the
 * styles they fit). A plain string is the untagged case — the tagging pass
 * only converts lines it actually reviewed, so existing content is untouched.
 */
export type BankEntry = string | { readonly text: string; readonly styles: readonly StyleId[] };

/** personaId -> event -> locale -> lines. Every persona × event × locale must have >= 1 line (tested). */
export type Banks = Readonly<Record<string, Readonly<Record<PersonaEvent, Readonly<Record<Locale, readonly BankEntry[]>>>>>>;

const CORE_BANKS: Readonly<Record<string, Readonly<Record<CoreEvent, Readonly<Record<Locale, readonly BankEntry[]>>>>>> = {
  amara: {
    'game-start': {
      en: ['Let’s have fun out there!', 'Ooh, I love this game. Good luck, friend!'],
      fr: ['On va bien s’amuser !', 'Oh, j’adore ce jeu. Bonne chance, mon ami !'],
      es: ['¡Vamos a divertirnos!', 'Me encanta este juego. ¡Buena suerte, amigo!'],
    },
    win: {
      en: ['That was so much fun — well played!', 'Yay me! But you were great too.'],
      fr: ['C’était super — bien joué !', 'Youpi ! Mais tu as été génial aussi.'],
      es: ['¡Qué divertido! ¡Bien jugado!', '¡Yupi! Pero tú también fuiste genial.'],
    },
    lose: {
      en: ['You got me! Rematch soon?', 'Well done — you played it just right.'],
      fr: ['Tu m’as eue ! Revanche bientôt ?', 'Bravo — tu as joué exactement comme il fallait.'],
      es: ['¡Me ganaste! ¿Revancha pronto?', 'Bien hecho: jugaste exactamente como había que hacer.'],
    },
    encourage: {
      en: ['Everyone makes that move sometimes — you’ll get the next one!', 'Almost! I liked your idea, though.'],
      fr: ['Ça arrive à tout le monde — tu auras le prochain !', 'Presque ! Mais j’ai aimé ton idée.'],
      es: ['A todos nos pasa: ¡la próxima la tienes!', '¡Casi! Pero me gustó tu idea.'],
    },
  },
  mateo: {
    'game-start': {
      en: [{ text: 'No idea what I’m doing — let’s go!', styles: ['chaotic'] }, 'Watch this! …okay, watch it in a minute.'],
      fr: [{ text: 'Aucune idée de ce que je fais — allons-y !', styles: ['chaotic'] }, 'Regarde ça ! … bon, regarde dans une minute.'],
      es: [{ text: 'Ni idea de lo que hago. ¡Vamos!', styles: ['chaotic'] }, '¡Mira esto! …bueno, míralo en un minuto.'],
    },
    win: {
      en: ['WHOO! Did you see that?! I didn’t even!', 'I won! I actually won! Tell everyone!'],
      fr: ['YOUHOU ! Tu as vu ?! Moi non plus !', 'J’ai gagné ! Vraiment gagné ! Dis-le à tout le monde !'],
      es: ['¡GUAU! ¿Viste eso? ¡Yo tampoco!', '¡Gané! ¡De verdad gané! ¡Se lo dices a todos!'],
    },
    lose: {
      en: ['Aww. Okay, that was kind of amazing though.', 'Next time! …probably. Maybe. Let’s see!'],
      fr: ['Ohh. Bon, c’était un peu génial quand même.', 'La prochaine ! … probablement. Peut-être. On verra !'],
      es: ['Oh… bueno, fue algo increíble igual.', '¡La próxima! …probablemente. A lo mejor. ¡Ya veremos!'],
    },
    encourage: {
      en: ['I make that one all the time. ALL the time.', 'Oops! Don’t worry, my moves are worse.'],
      fr: ['Je fais ça tout le temps. TOUT le temps.', 'Oups ! T’inquiète, mes coups sont pires.'],
      es: ['Yo hago eso constantemente. ¡CONSTANTEMENTE!', '¡Uy! No te preocupes, mis jugadas son peores.'],
    },
  },
  layla: {
    'game-start': {
      en: ['Ooh, I wonder what we’ll discover this time.', { text: 'New game, new ideas. I can’t wait!', styles: ['eager'] }],
      fr: ['Oh, qu’est-ce qu’on va découvrir cette fois ?', { text: 'Nouvelle partie, nouvelles idées. J’ai hâte !', styles: ['eager'] }],
      es: ['Qué descubriremos esta vez.', { text: 'Partida nueva, ideas nuevas. ¡Tengo ganas!', styles: ['eager'] }],
    },
    win: {
      en: ['That was a lovely game — thank you!', 'I learned so much just now. And I won!'],
      fr: ['C’était une belle partie — merci !', 'J’ai tellement appris. Et j’ai gagné !'],
      es: ['Fue una partida preciosa: ¡gracias!', 'He aprendido mucho. ¡Y he ganado!'],
    },
    lose: {
      en: ['Clever! I’m writing that one down.', 'You taught me something there. Well done!'],
      fr: ['Malin ! Je note cette idée.', 'Tu m’as appris quelque chose. Bravo !'],
      es: ['¡Listo! Apunto esa idea.', 'Me has enseñado algo. ¡Bien hecho!'],
    },
    encourage: {
      en: ['Hmm, tricky one! Try looking at my side of the board.', 'Mistakes are how we learn. Truly!'],
      fr: ['Hmm, celle-là était coriace ! Regarde plutôt mon côté du plateau.', 'Les erreurs, c’est comme ça qu’on apprend. Vraiment !'],
      es: ['Vaya, ¡esta era difícil! Mira mejor mi lado del tablero.', 'Los errores son como aprendemos. ¡De verdad!'],
    },
  },
  kenji: {
    'game-start': {
      en: ['Interesting. Let’s see what you’re thinking.', { text: 'I have a plan. Two, actually.', styles: ['tricky'] }],
      fr: ['Intéressant. Voyons voir ce que tu penses.', { text: 'J’ai un plan. Deux, en fait.', styles: ['tricky'] }],
      es: ['Interesante. Veamos qué piensas.', { text: 'Tengo un plan. Dos, en realidad.', styles: ['tricky'] }],
    },
    win: {
      en: [{ text: 'Good game. My traps worked — this time.', styles: ['tricky'] }, 'Exactly as planned. You nearly had me.'],
      fr: [{ text: 'Belle partie. Mes pièges ont marché — cette fois.', styles: ['tricky'] }, 'Exactement comme prévu. Tu m’as failli avoir.'],
      es: [{ text: 'Buena partida. Mis trampas funcionaron: esta vez.', styles: ['tricky'] }, 'Exactamente según lo planeado. Casi me ganas.'],
    },
    lose: {
      en: ['Well played. That move surprised me — well done.', 'You saw through my plan. Impressive.'],
      fr: ['Bien joué. Ce coup m’a surpris — bravo.', 'Tu as vu mon plan. Impressionnant.'],
      es: ['Bien jugado. Esa jugada me sorprendió: enhorabuena.', 'Viste mi plan. Impresionante.'],
    },
    encourage: {
      en: ['Careful — something is guarding that square.', 'Think about what I want first. Then decide.'],
      fr: ['Attention — quelque chose garde cette case.', 'Pense d’abord à ce que JE veux. Puis décide.'],
      es: ['Cuidado: algo defiende esa casilla.', 'Piensa primero qué quiero yo. Luego decide.'],
    },
  },
  priya: {
    'game-start': {
      en: [{ text: 'Slow and steady. Good luck!', styles: ['cautious'] }, { text: 'I’ll keep things safe — you be brave.', styles: ['cautious'] }],
      fr: [{ text: 'Rien ne presse. Bonne chance !', styles: ['cautious'] }, { text: 'Moi, je joue la sécurité — à toi d’être courageux.', styles: ['cautious'] }],
      es: [{ text: 'Paso a paso. ¡Buena suerte!', styles: ['cautious'] }, { text: 'Yo jugaré a lo seguro; tú sé valiente.', styles: ['cautious'] }],
    },
    win: {
      en: ['Thank you for the game — that was close!', 'Slow and steady, right? Well played by you!'],
      fr: ['Merci pour la partie — c’était serré !', 'Rien ne presse, n’est-ce pas ? Et bien joué !'],
      es: ['Gracias por la partida: ¡estuvo reñida!', 'Paso a paso, ¿verdad? ¡Y bien jugado por ti!'],
    },
    lose: {
      en: ['Oh! You broke right through. Nicely done.', 'That was brave — and it worked. Respect!'],
      fr: ['Oh ! Tu as tout percé. Très bien joué.', 'C’était courageux — et ça a marché. Respect !'],
      es: ['¡Oh! Rompiste mi defensa. Muy bien hecho.', 'Eso fue valiente, y funcionó. ¡Respeto!'],
    },
    encourage: {
      en: ['Count the defenders before you take — that helps me too!', { text: 'Keep your pieces near their friends.', styles: ['cautious'] }],
      fr: ['Compte les défenseurs avant de prendre — ça m’aide aussi !', { text: 'Garde tes pièces près de leurs copines.', styles: ['cautious'] }],
      es: ['Cuenta los defensores antes de capturar: ¡a mí también me sirve!', { text: 'Mantén tus piezas cerca de sus amigas.', styles: ['cautious'] }],
    },
  },
  oliver: {
    'game-start': {
      en: ['I have SO many plans. At least three!', { text: 'Let’s go, let’s go, let’s go!', styles: ['chaotic'] }],
      fr: ['J’ai TELLEMENT de plans. Au moins trois !', { text: 'On y va, on y va, on y va !', styles: ['chaotic'] }],
      es: ['Tengo MUCHOS planes. ¡Al menos tres!', { text: '¡Vamos, vamos, vamos!', styles: ['chaotic'] }],
    },
    win: {
      en: ['YES! One of my plans worked!', 'I won! Did you see which plan? Neither did I!'],
      fr: ['OUI ! Un de mes plans a marché !', 'J’ai gagné ! Tu as vu lequel des plans ? Moi non plus !'],
      es: ['¡SÍ! ¡Uno de mis planes funcionó!', '¡Gané! ¿Viste qué plan? ¡Yo tampoco!'],
    },
    lose: {
      en: ['Good game! I’ll think of something new, promise.', 'You’re really good. I mean it!'],
      fr: ['Bonne partie ! Je trouverai quelque chose de nouveau, promis.', 'Tu es vraiment fort(e). Je le pense !'],
      es: ['¡Buena partida! Pensaré algo nuevo, prometido.', 'Eres muy bueno. ¡En serio!'],
    },
    encourage: {
      en: ['That happens to me like… every move.', 'It’s okay! My best ideas come after mistakes.'],
      fr: ['Ça me fait ça à… chaque coup, à peu près.', 'C’est rien ! Mes meilleures idées viennent après les erreurs.'],
      es: ['A mí me pasa eso… en cada jugada, más o menos.', '¡No pasa nada! Mis mejores ideas vienen después de los errores.'],
    },
  },
  marisol: {
    'game-start': {
      en: ['A pleasure. Play your best — I will play mine.', { text: 'Take your time. Think about what you want.', styles: ['solid'] }],
      fr: ['Avec plaisir. Joue ton mieux — je jouerai au mien.', { text: 'Prends ton temps. Pense à ce que tu veux.', styles: ['solid'] }],
      es: ['Un placer. Juega lo mejor que puedas; yo haré lo mismo.', { text: 'Tómate tu tiempo. Piensa en lo que quieres.', styles: ['solid'] }],
    },
    win: {
      en: ['A fine game. You pushed me to the end.', 'Well fought. You should be proud.'],
      fr: ['Une belle partie. Tu m’as tenue jusqu’au bout.', 'Bien joué. Tu peux être fier(ère).'],
      es: ['Una buena partida. Me has empujado hasta el final.', 'Bien luchado. Debes estar orgulloso(orgullosa).'],
    },
    lose: {
      en: ['Congratulations — you earned every point of that.', 'Beautifully played. Remember this game.'],
      fr: ['Félicitations — tu as mérité chaque point.', 'Magnifiquement joué. Souviens-toi de cette partie.'],
      es: ['Enhorabuena: te has ganado cada punto.', 'Jugada magnífica. Recuerda esta partida.'],
    },
    encourage: {
      en: ['Look again — what is my weakest piece doing?', 'A setback is information. What did it tell you?'],
      fr: ['Regarde encore — que fait ma pièce la plus faible ?', 'Un revers, c’est une information. Que t’a-t-elle dit ?'],
      es: ['Mira otra vez: ¿qué hace mi pieza más débil?', 'Un revés es información. ¿Qué te ha dicho?'],
    },
  },
  kofi: {
    'game-start': {
      en: ['Sit down, little one. Let’s have a good, long think together.', 'I have played many games. Every one starts the same: with a smile.'],
      fr: ['Assieds-toi, petit. Réfléchissons bien ensemble.', 'J’ai joué beaucoup de parties. Toutes commencent pareil : avec un sourire.'],
      es: ['Siéntate, pequeño. Pensemos juntos con calma.', 'He jugado muchas partidas. Todas empiezan igual: con una sonrisa.'],
    },
    win: {
      en: ['A good game, honestly played. Thank you.', 'Experience won today. Tomorrow, who can say?'],
      fr: ['Une honnête partie. Merci.', 'L’expérience a gagné aujourd’hui. Demain, qui sait ?'],
      es: ['Una partida honesta. Gracias.', 'Hoy ganó la experiencia. Mañana, ¿quién sabe?'],
    },
    lose: {
      en: ['Wonderful! You play with your head AND your heart.', 'The student becomes the teacher. Well done, little one.'],
      fr: ['Magnifique ! Tu joues avec la tête ET le cœur.', 'L’élève dépasse le maître. Bravo, petit.'],
      es: ['¡Magnífico! Juegas con la cabeza Y con el corazón.', 'El alumno supera al maestro. Bien hecho, pequeño.'],
    },
    encourage: {
      en: ['Do not worry. The board forgives everyone. Look again.', 'When I was your age, I made that move too.'],
      fr: ['Ne t’inquiète pas. Le plateau pardonne tout. Regarde encore.', 'Quand j’avais ton âge, je faisais ce coup aussi.'],
      es: ['No te preocupes. El tablero lo perdona todo. Mira otra vez.', 'Cuando yo tenía tu edad, hacía esa misma jugada.'],
    },
  },
  karim: {
    'game-start': {
      en: ['Ready when you are.', { text: 'I won’t show you everything. Only some of it.', styles: ['tricky'] }],
      fr: ['Quand tu veux.', { text: 'Je ne vais pas tout te montrer. Juste un peu.', styles: ['tricky'] }],
      es: ['Cuando quieras.', { text: 'No te lo voy a mostrar todo. Solo una parte.', styles: ['tricky'] }],
    },
    win: {
      en: ['Good game. Calm beats clever, sometimes.', 'You played well. I just played patiently.'],
      fr: ['Belle partie. Le calme bat la malice, parfois.', 'Tu as bien joué. Moi, j’ai joué patiemment.'],
      es: ['Buena partida. La calma vence a la astucia, a veces.', 'Jugaste bien. Yo jugué con paciencia.'],
    },
    lose: {
      en: ['Hm. Well spotted — all of it.', 'You stayed calm and it paid off. Well done.'],
      fr: ['Hm. Bien vu — tout ça.', 'Tu es resté calme et ça a payé. Bravo.'],
      es: ['Vaya. Bien visto, todo.', 'Te mantuviste en calma y funcionó. Bien hecho.'],
    },
    encourage: {
      en: [{ text: 'That was a trap, but not a mean one. Look again.', styles: ['tricky'] }, 'Ask yourself: what changed on my last move?'],
      fr: [{ text: 'C’était un piège, mais pas méchant. Regarde encore.', styles: ['tricky'] }, 'Demande-toi : qu’est-ce qui a changé à mon dernier coup ?'],
      es: [{ text: 'Era una trampa, pero no mala. Mira otra vez.', styles: ['tricky'] }, 'Pregúntate: ¿qué cambió en mi última jugada?'],
    },
  },
  hannah: {
    'game-start': {
      en: ['I’ll think first, then act. You do what you like!', { text: 'Remember: protected pieces are happy pieces.', styles: ['cautious'] }],
      fr: ['Je réfléchis d’abord, puis j’agis. Toi, fais comme tu veux !', { text: 'Souviens-toi : une pièce protégée est une pièce contente.', styles: ['cautious'] }],
      es: ['Primero pienso, luego actúo. ¡Tú haz lo que quieras!', { text: 'Recuerda: las piezas protegidas son piezas felices.', styles: ['cautious'] }],
    },
    win: {
      en: ['Yes! Thinking first really does work.', 'Good game — you made me think so hard.'],
      fr: ['Oui ! Réfléchir d’abord, ça marche vraiment.', 'Bonne partie — tu m’as fait réfléchir très fort.'],
      es: ['¡Sí! Primero pensar funciona de verdad.', 'Buena partida: me has hecho pensar muchísimo.'],
    },
    lose: {
      en: ['Ooh, well done! Your plan was better.', 'I protected everything except the thing that mattered!'],
      fr: ['Oh, bien joué ! Ton plan était meilleur.', 'J’ai tout protégé, sauf la chose importante !'],
      es: ['¡Oh, bien hecho! Tu plan era mejor.', '¡Protegé todo menos lo que importaba!'],
    },
    encourage: {
      en: [{ text: 'Before you move — is anything of yours unprotected?', styles: ['cautious'] }, 'That’s fine. Count the attackers, then decide.'],
      fr: [{ text: 'Avant de jouer — as-tu une pièce sans protection ?', styles: ['cautious'] }, 'C’est rien. Compte les attaquants, puis décide.'],
      es: [{ text: 'Antes de mover: ¿tienes alguna pieza sin proteger?', styles: ['cautious'] }, 'Está bien. Cuenta los atacantes y decide.'],
    },
  },
  wei: {
    'game-start': {
      en: ['Fair and friendly — that’s the rule. Ready?', 'A good game is one where both players learn something.'],
      fr: ['Loyal et sympa — c’est la règle. Prêt ?', 'Une bonne partie, c’est celle où les deux apprennent quelque chose.'],
      es: ['Justo y amistoso: esa es la regla. ¿Listo?', 'Una buena partida es la que enseña algo a los dos.'],
    },
    win: {
      en: ['Great game — you had me worried in the middle!', 'Well played all round. Let’s do it again soon.'],
      fr: ['Superbe partie — tu m’as inquiété au milieu !', 'Bien joué partout. On remet ça vite.'],
      es: ['Gran partida: ¡me tuviste preocupado en el medio!', 'Bien jugado por todos lados. Repetimos pronto.'],
    },
    lose: {
      en: ['Clean and clever. You deserved that.', 'Now THAT was a plan. Congratulations!'],
      fr: ['Net et malin. Tu l’as mérité.', 'Ça, c’était un plan. Félicitations !'],
      es: ['Limpio y listo. Te lo merecías.', 'ESO era un plan. ¡Enhorabuena!'],
    },
    encourage: {
      en: [{ text: 'Take a breath. The best move is often the quiet one.', styles: ['solid'] }, 'Two of your pieces can be saved. Find them!'],
      fr: [{ text: 'Respire. Le meilleur coup est souvent le plus discret.', styles: ['solid'] }, 'Deux de tes pièces peuvent être sauvées. Trouve-les !'],
      es: [{ text: 'Respira. La mejor jugada suele ser la más discreta.', styles: ['solid'] }, 'Dos de tus piezas se pueden salvar. ¡Encuéntralas!'],
    },
  },
  fatima: {
    'game-start': {
      en: ['Come, sit! Grandma plays for fun — mostly.', 'You remind me of my grandchildren. They never let me win, either!'],
      fr: ['Viens, assieds-toi ! Mamie joue pour le plaisir — enfin, surtout.', 'Tu me rappelles mes petits-enfants. Eux non plus ne me laissent jamais gagner !'],
      es: ['Ven, siéntate. La abuela juega por diversión… casi siempre.', 'Me recuerdas a mis nietos. ¡Ellos tampoco me dejan ganar!'],
    },
    win: {
      en: ['Oh ho! Grandma still has it.', 'A sweet little game. You nearly had me, dear.'],
      fr: ['Oh ho ! Mamie a encore la forme.', 'Une petite partie délicieuse. Tu m’as failli avoir, chéri.'],
      es: ['¡Oh, oh! La abuela todavía vale.', 'Una partida dulce. Casi me ganas, cariño.'],
    },
    lose: {
      en: ['Marvelous! You play with such patience.', 'Wonderful. Come back and play with me again, dear.'],
      fr: ['Formidable ! Tu joues avec tant de patience.', 'Merveilleux. Reviens jouer avec moi, chéri.'],
      es: ['¡Maravilloso! Juegas con tanta paciencia.', 'Estupendo. Vuelve a jugar conmigo, cariño.'],
    },
    encourage: {
      en: ['Never mind, dear. Even grandmas make mistakes.', 'Look at the whole board, not just your corner.'],
      fr: ['Ce n’est rien, chéri. Même les grands-mères font des erreurs.', 'Regarde tout le plateau, pas seulement ton coin.'],
      es: ['No pasa nada, cariño. Hasta las abuelas se equivocan.', 'Mira el tablero entero, no solo tu esquina.'],
    },
  },
};

/** Core events (chess/checkers/game result) merged with the table events (tricks and rounds). */
export const BANKS: Banks = Object.fromEntries(
  Object.entries(CORE_BANKS).map(([id, core]) => [id, { ...core, ...TABLE_BANKS[id]! }]),
) as Banks;
